/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Box, Grid, TextField, MenuItem, IconButton } from '@mui/material';
import { Close, Add } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { CustomTextField, SelectDropdown } from '../../index';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ContainedButton } from '../../index';
import { useState } from 'react';

export const WorkExperienceTab = (props) => {
  const handleChange = (index, field, value) => {
    props.setEmpWorkExp((prev) => {
      const updated = [...prev];

      if (field === 'monthlysalary') {
        const parsed = value === '' ? undefined : parseFloat(value);
        updated[index] = { ...updated[index], [field]: parsed };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }

      return updated;
    });
  };

  const handleAdd = () => {
    props.setEmpWorkExp((prev) => [
      {
        empid: props.empid,
        startdate: null,
        enddate: null,
        position: '',
        placeofemployment: '',
        monthlysalary: undefined,
        status: '',
        government: false,
      },
      ...prev,
    ]);
  };

  const handleDelete = (index) => {
    props.setEmpWorkExp((prev) => prev.filter((_, i) => i !== index));
  };

  const boxStyle = {
    mx: 0,
    my: 1,
    px: 3,
    py: 2,
  };
  const [startTouched, setStartTouched] = useState(false);

  return (
    <Box sx={{ ...boxStyle, maxHeight: '400px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        {!props.viewOnly && (
          <ContainedButton
            startIcon={<Add />}
            onClick={handleAdd}
            variant="contained"
            btnTitle={'Add Work Experience'}
            style={{ width: 'auto' }}
          />
        )}
      </Box>
      {props.empWorkExp.map((exp, index) => (
        <Box
          key={index}
          sx={{
            bgcolor: 'rgb(26, 32, 44, 0.6)',
            display: 'flex',
            justifyContent: 'start',
            p: 2,
            mt: 2,
            borderRadius: '5px',
            position: 'relative',
          }}
        >
          {!props.viewOnly && (
            <IconButton
              aria-label="delete"
              onClick={() => handleDelete(index)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                mr: 1,
              }}
            >
              <Close />
            </IconButton>
          )}
          <Grid container columnSpacing={2} sx={{ mt: 3 }}>
            <Grid item xs={6}>
              <CustomTextField
                error={!exp.placeofemployment}
                errorMsg="Please enter a place of employment"
                label="Company/Institution"
                value={exp.placeofemployment || ''}
                onChange={(e) =>
                  handleChange(index, 'placeofemployment', e.target.value)
                }
                viewOnly={props.viewOnly}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                error={!exp.position}
                errorMsg="Please enter a position"
                label="Position"
                value={exp.position || ''}
                onChange={(e) =>
                  handleChange(index, 'position', e.target.value)
                }
                viewOnly={props.viewOnly}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={exp.startdate ? new Date(exp.startdate) : null}
                  maxDate={exp.enddate ? new Date(exp.enddate) : undefined}
                  onChange={(newValue) =>
                    handleChange(index, 'startdate', newValue)
                  }
                  disableOpenPicker={props.viewOnly}
                  readOnly={props.viewOnly}
                  renderInput={(params) => {
                    const showError = startTouched && !exp.startdate;
                    return (
                      <TextField
                        onFocus={() => setStartTouched(true)}
                        margin="normal"
                        fullWidth
                        required
                        {...params}
                        error={showError}
                        helperText={showError ? 'Start Date is required' : ''}
                        inputProps={{
                          ...params.inputProps,
                          style: {
                            cursor: props.viewOnly ? 'default' : undefined,
                          },
                        }}
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={exp.enddate ? new Date(exp.enddate) : null}
                  minDate={exp.startdate ? new Date(exp.startdate) : undefined}
                  onChange={(newValue) =>
                    handleChange(index, 'enddate', newValue)
                  }
                  disableOpenPicker={props.viewOnly}
                  readOnly={props.viewOnly}
                  renderInput={(params) => (
                    <TextField
                      margin="normal"
                      fullWidth
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        style: {
                          cursor: props.viewOnly ? 'default' : undefined,
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                type="number"
                errorMsg="Please enter a monthly salary"
                label="Salary"
                value={exp.monthlysalary || ''}
                onChange={(e) =>
                  handleChange(index, 'monthlysalary', e.target.value)
                }
                viewOnly={props.viewOnly}
              />
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                errorMsg="Please enter a status"
                label="Status"
                value={exp.status || ''}
                onChange={(e) => handleChange(index, 'status', e.target.value)}
                viewOnly={props.viewOnly}
              />
            </Grid>

            <Grid item xs={6}>
              <SelectDropdown
                fullWidth
                require
                margin="normal"
                required
                label="Government Employee"
                value={exp.government ?? ''}
                onChange={(e) =>
                  handleChange(index, 'government', e.target.value)
                }
                viewOnly={props.viewOnly}
              >
                <MenuItem value={false}>No</MenuItem>
                <MenuItem value={true}>Yes</MenuItem>
              </SelectDropdown>
            </Grid>
          </Grid>
        </Box>
      ))}
    </Box>
  );
};
